import { BrandingProvider, GLToastProvider } from '@group-link-one/gl-components';
import { Global } from '@emotion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FlagsProvider, type FlagsmithConfig, type User } from '@group-link-one/gl-utils';
import { UTLoading } from '@group-link-one/gl-ui/utilities';
import { useMemo } from 'react';
import { BrandingRegistry } from './Branding/BrandingRegistry';
import { Routes } from './Routes/Routes';
import { PageLayoutProvider } from './Templates/Main/PageLayout/Context/PageLayoutContext';
import { ToastStack } from '@/Shared/components/ToastStack/ToastStack';
import { useAutoLogout } from '@/Auth';
import { ProfileProvider, useProfile } from '@/Auth/contexts/ProfileContext';
import { env } from '@/config/env';
import { designTokens } from '@/Styles/designTokens';

function AppContent() {
  const { profile, loading } = useProfile();

  // Automatically logout when TTL expires
  useAutoLogout({
    enabled: true,
    checkInterval: 60000, // Check every minute
    onAutoLogout: () => {
      // eslint-disable-next-line no-console
      console.log('Session expired - logging out automatically');
    },
  });

  // Memoize flags config to prevent unnecessary re-initialization
  const flagsConfig: FlagsmithConfig = useMemo(
    () => ({
      environmentID: env.flagsmith.environmentId,
      apiUrl: env.flagsmith.apiUrl,
      cacheFlags: false,
      enableLogs: false,
    }),
    [],
  );

  // Memoize user object to prevent unnecessary Flags re-fetches
  // Only recreate when profile changes, not on every render
  const user: User | undefined = useMemo(() => {
    if (!profile) return undefined;

    return {
      id: 0,
      name: profile.name.split(' ')[0] || '',
      last_name: profile.name.split(' ').slice(1).join(' ') || '',
      session_id: 0,
      modules: [],
      privileges: profile.permissions,
      // BFF Apps admins are never impersonated — these are the panel's own users.
      impersonator_id: null,
      org: {
        id: 0,
        name: '',
      },
      org_occupational_area: 'none',
      email: profile.email,
      use_case: 'util_water',
    };
  }, [profile]);

  // Wait for profile to load before rendering the app
  if (loading) {
    return <UTLoading screen='as-full-screen' type='dots' />;
  }

  return (
    <FlagsProvider config={flagsConfig} user={user}>
      <GLToastProvider>
        <PageLayoutProvider>
          <Routes />
          <ToastStack />
        </PageLayoutProvider>
      </GLToastProvider>
    </FlagsProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandingProvider brandingRegistry={BrandingRegistry} initialBranding={env.branding}>
        <Global styles={designTokens()} />
        <ProfileProvider>
          <AppContent />
        </ProfileProvider>
      </BrandingProvider>
    </QueryClientProvider>
  );
}

export default App;
